import { PlatformError } from '@djgg/core';

export type JsonValue = null | boolean | number | string | JsonValue[] | JsonObject;
export interface JsonObject { [key: string]: JsonValue }

/** Limits apply before hashing/validation; release identity is never truncated. */
export const CONTRACT_JSON_LIMITS = Object.freeze({ depth: 32, nodes: 50_000, array: 10_000,
  string: 65_536, bytes: 1_048_576 });
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

export function contractError(path: string, message: string): never {
  throw new PlatformError('validation', `${path}: ${message}`);
}

/** Strict data-only clone. Inspect descriptors before reading, so accessors and
 * toJSON hooks never run. Arrays remain ordered; object keys use code-unit order. */
export function cloneJson(value: unknown): JsonValue {
  const ancestors = new Set<object>();
  let nodes = 0;
  function walk(v: unknown, path: string, depth: number): JsonValue {
    if (++nodes > CONTRACT_JSON_LIMITS.nodes) contractError(path, 'JSON node limit exceeded');
    if (depth > CONTRACT_JSON_LIMITS.depth) contractError(path, 'JSON nesting limit exceeded');
    if (v === null || typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      if (v.length > CONTRACT_JSON_LIMITS.string) contractError(path, 'string too long');
      return v;
    }
    if (typeof v === 'number') {
      if (!Number.isFinite(v) || (Number.isInteger(v) && !Number.isSafeInteger(v))) {
        contractError(path, 'expected a finite, precisely representable JSON number');
      }
      return Object.is(v, -0) ? 0 : v;
    }
    if (typeof v !== 'object') contractError(path, 'expected JSON data (no undefined, functions or bigint)');
    const array = Array.isArray(v);
    const prototype = Object.getPrototypeOf(v);
    if (array ? prototype !== Array.prototype : prototype !== Object.prototype && prototype !== null) {
      contractError(path, 'expected plain JSON object/array');
    }
    if (ancestors.has(v)) contractError(path, 'cyclic JSON');
    if (Object.getOwnPropertySymbols(v).length) contractError(path, 'symbol keys are not JSON');
    ancestors.add(v);
    const descriptors = Object.getOwnPropertyDescriptors(v);
    let result: JsonValue;
    if (array) {
      if (v.length > CONTRACT_JSON_LIMITS.array) contractError(path, 'array too long');
      if (Object.keys(descriptors).length !== v.length + 1) contractError(path, 'sparse or decorated array');
      const out: JsonValue[] = [];
      for (let i = 0; i < v.length; i++) {
        const d = descriptors[String(i)];
        if (!d || !('value' in d) || !d.enumerable) contractError(`${path}[${i}]`, 'expected enumerable data element');
        out.push(walk(d.value, `${path}[${i}]`, depth + 1));
      }
      result = out;
    } else {
      const entries: Array<[string, JsonValue]> = [];
      for (const key of Object.keys(descriptors).sort()) {
        if (UNSAFE_KEYS.has(key)) contractError(`${path}.${key}`, 'unsafe object key');
        const d = descriptors[key]!;
        if (!('value' in d) || !d.enumerable) contractError(`${path}.${key}`, 'expected enumerable data property');
        entries.push([key, walk(d.value, `${path}.${key}`, depth + 1)]);
      }
      result = Object.fromEntries(entries);
    }
    ancestors.delete(v);
    return result;
  }
  const result = walk(value, '$', 0);
  if (new TextEncoder().encode(JSON.stringify(result)).length > CONTRACT_JSON_LIMITS.bytes) {
    contractError('$', 'encoded JSON byte limit exceeded');
  }
  return result;
}

/** Exact finite numbers and ordered arrays; unlike rounded snapshot comparison. */
export function canonicalJson(value: unknown): string {
  // JSON.stringify orders integer-looking keys numerically. Emit objects here
  // to guarantee the declared code-unit ordering for ALL property names.
  function encode(v: JsonValue): string {
    if (v === null || typeof v !== 'object') return JSON.stringify(v);
    if (Array.isArray(v)) return `[${v.map(encode).join(',')}]`;
    return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${encode(v[k]!)}`).join(',')}}`;
  }
  return encode(cloneJson(value));
}

export function validateJsonObject(value: unknown, path = '$'): JsonObject {
  const parsed = cloneJson(value);
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) contractError(path, 'expected JSON object');
  return parsed;
}

export async function sha256Bytes(bytes: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new Uint8Array(bytes));
  return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
}

export function canonicalDigest(value: unknown): Promise<string> {
  return sha256Bytes(new TextEncoder().encode(canonicalJson(value)));
}

export function validateSha256(value: unknown, path = 'sha256'): string {
  if (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value)) contractError(path, 'expected 64 lowercase SHA-256 hex characters');
  return value;
}

export function freezeJson<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === 'object') {
    for (const child of Object.values(value)) freezeJson(child);
    Object.freeze(value);
  }
  return value;
}
